/**
 * Pure helpers for timeseries hover labels on a date x-axis (epoch-ms coordinates).
 *
 * Plotly `x unified` shows a single formatted date when the first trace supplies
 * `text` + `%{text}` in its hovertemplate, the x-axis uses `type: 'date'` with
 * `hoverformat: ' '`, and follower traces omit the date line.
 *
 * @packageDocumentation
 * @module TimeseriesHoverLabels
 * @public
 */

import { TimeseriesAxisLabelMode } from '../config/plotTypes';
import { resolveTimeseriesHoverLeaderIndex } from './curveSessionVisibility';
import {
  EpochMillisecondRange,
  formatUtcOffsetSuffix,
  selectTickStepMs, TIMESERIES_XAXIS_HOVERFORMAT, TimeseriesTickGranularity
} from './timeseriesAxisTicks';
import { formatTimeseriesHoverLabel, resolveGranularityFromStepMs } from './timeseriesTickLabelFormat';

// -- type definitions --

/**
 * @deprecated X labels now live in `trace.text`; kept for API compatibility.
 */
export const TIMESERIES_HOVER_X_CUSTOMDATA_INDEX = 0;

/** One hover label row attached to a trace point. */
export interface TimeseriesHoverLabelRow {
  readonly epochMs: number;
  readonly formattedTimestamp: string;
}

/** Input for enriching a single trace with hover timestamps. */
export interface AttachTimeseriesHoverLabelsInput {
  readonly epochMsValues: readonly number[];
  readonly displayTimezone: string;
  readonly labelMode: TimeseriesAxisLabelMode;
  /** Granularity inferred from visible range; defaults to minute in pipeline. */
  readonly granularity?: TimeseriesTickGranularity;
  readonly locale?: string | string[];
}

/** Minimal trace shape for hover enrichment. */
export interface TimeseriesHoverTraceLike {
  readonly x?: Plotly.Datum[] | Plotly.Datum[][] | Plotly.TypedArray;
  readonly text?: string | string[];
  readonly customdata?: unknown;
  readonly hovertemplate?: string | string[];
  readonly visible?: boolean | 'legendonly';
}

const DEFAULT_HOVER_GRANULARITY: TimeseriesTickGranularity = 'minute';

const DEFAULT_STANDALONE_HOVER_BODY =
  '<b>%{fullData.name}</b>: %{y:.2f}<extra></extra>';

// -- helper functions --

/**
 * Normalizes one trace point customdata value into an array.
 *
 * @param value - Scalar, array, null, or undefined customdata for one point.
 * @returns Array form suitable for merging with other hover fields.
 */
export function normalizePointCustomdata(value: unknown): unknown[] {
  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return [...value];
  }

  return [value];
}

/**
 * Shifts `%{customdata…}` references in a Plotly hovertemplate by a delta.
 *
 * @param template - Hovertemplate string.
 * @param delta - Index offset to add to each customdata reference.
 * @returns Template with updated customdata indices.
 */
export function shiftHovertemplateCustomdataIndices(
  template: string,
  delta: number
): string {
  return template.replace(
    /%\{customdata(?:\[(\d+)\])?(?::([^}]+))?\}/g,
    (_match, indexStr, formatSuffix) => {
      const index = indexStr !== undefined ? Number(indexStr) : 0;
      const newIndex = index + delta;

      if (formatSuffix !== undefined) {
        return `%{customdata[${newIndex}]:${formatSuffix}}`;
      }

      if (indexStr !== undefined) {
        return `%{customdata[${newIndex}]}`;
      }

      return `%{customdata[${newIndex}]}`;
    }
  );
}

function datumToEpochMs(value: Plotly.Datum): number {
  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  return Number.NaN;
}

function toTraceEpochMsValues(
  x: Plotly.Datum[] | Plotly.Datum[][] | Plotly.TypedArray | undefined
): number[] {
  if (!x) {
    return [];
  }

  const values = Array.isArray(x)
    ? Array.isArray(x[0])
      ? (x as Plotly.Datum[][]).flat()
      : (x as Plotly.Datum[])
    : Array.from(x as ArrayLike<number>);

  return values
    .map((value) => datumToEpochMs(value))
    .filter((value) => Number.isFinite(value));
}

/**
 * Converts trace x values to epoch milliseconds for a linear timeseries axis.
 *
 * @param x - Trace x array that may contain Date objects, numbers, or ISO strings.
 * @returns Epoch-ms x values aligned with the input order.
 */
export function normalizeTraceXToEpochMs(
  x: Plotly.Datum[] | Plotly.Datum[][] | Plotly.TypedArray | undefined
): number[] | undefined {
  if (!x) {
    return undefined;
  }

  const epochMsValues = toTraceEpochMsValues(x);

  if (epochMsValues.length === 0) {
    return undefined;
  }

  return epochMsValues;
}

/**
 * Formats one hover timestamp with short full date and time to milliseconds.
 *
 * Hover always uses extended millisecond labels. A UTC offset suffix is appended
 * for tooltip clarity (matching axis label suffix style).
 *
 * @param epochMs - Trace x value in epoch milliseconds.
 * @param displayTimezone - Effective IANA timezone.
 * @param _granularity - Ignored; hover always uses millisecond detail.
 * @param labelMode - Conditional or global suffix mode for UTC offset.
 * @param locale - Optional BCP 47 locale tag(s).
 * @returns Formatted hover timestamp string.
 */
export function formatTimeseriesHoverTimestamp(
  epochMs: number,
  displayTimezone: string,
  _granularity: TimeseriesTickGranularity,
  labelMode: TimeseriesAxisLabelMode,
  locale?: string | string[]
): string {
  void labelMode;
  const base = formatTimeseriesHoverLabel({
    epochMs,
    displayTimezone,
    locale,
  });
  const offsetSuffix = formatUtcOffsetSuffix(epochMs, displayTimezone);

  return `${base} ${offsetSuffix}`;
}

/**
 * Builds formatted x labels aligned with trace x values.
 *
 * @param input - Epoch values plus display settings.
 * @returns One formatted label per trace point.
 */
export function buildTimeseriesHoverLabelStrings(
  input: AttachTimeseriesHoverLabelsInput
): readonly string[] {
  const granularity = input.granularity ?? DEFAULT_HOVER_GRANULARITY;

  return input.epochMsValues.map((epochMs) =>
    formatTimeseriesHoverTimestamp(
      epochMs,
      input.displayTimezone,
      granularity,
      input.labelMode,
      input.locale
    )
  );
}

/**
 * Builds hover label rows aligned with trace x values.
 *
 * @param input - Epoch values plus display settings.
 * @returns One hover label row per trace point.
 */
export function buildTimeseriesHoverLabelRows(
  input: AttachTimeseriesHoverLabelsInput
): readonly TimeseriesHoverLabelRow[] {
  const granularity = input.granularity ?? DEFAULT_HOVER_GRANULARITY;

  return input.epochMsValues.map((epochMs) => ({
    epochMs,
    formattedTimestamp: formatTimeseriesHoverTimestamp(
      epochMs,
      input.displayTimezone,
      granularity,
      input.labelMode,
      input.locale
    ),
  }));
}

/**
 * @deprecated X labels are stored in `trace.text`; kept for API compatibility.
 */
export function prependTimeseriesHoverXLabelsToCustomdata(
  traceCustomdata: readonly unknown[] | undefined,
  labels: readonly string[]
): unknown[] {
  return labels.map((label, index) => {
    const rest = normalizePointCustomdata(traceCustomdata?.[index]);

    if (rest.length === 0) {
      return label;
    }

    return [label, ...rest];
  });
}

/** Blank x-axis hover format that suppresses Plotly's automatic date header. */
export { TIMESERIES_XAXIS_HOVERFORMAT } from './timeseriesAxisTicks';

/**
 * Builds the hovertemplate for the leader trace (first in the unified hover group).
 *
 * @param existingTemplate - Trace hovertemplate before timestamp enrichment.
 * @returns Hovertemplate with `%{text}` driving the unified x label once.
 */
export function buildTimeseriesLeaderHoverTemplate(
  existingTemplate?: string
): string {
  const body = existingTemplate ?? DEFAULT_STANDALONE_HOVER_BODY;
  return `%{text}<br>${body}`;
}

/**
 * Builds the hovertemplate for follower traces (no repeated date line).
 *
 * @param existingTemplate - Trace hovertemplate before timestamp enrichment.
 * @returns Hovertemplate without a `%{text}` prefix.
 */
export function buildTimeseriesFollowerHoverTemplate(
  existingTemplate?: string
): string {
  return existingTemplate ?? DEFAULT_STANDALONE_HOVER_BODY;
}

/**
 * @deprecated Use {@link buildTimeseriesLeaderHoverTemplate} for the first trace.
 */
export function buildTimeseriesUnifiedHoverTemplate(
  existingTemplate?: string
): string {
  return buildTimeseriesLeaderHoverTemplate(existingTemplate);
}

/**
 * Enriches timeseries traces for unified hover with a single shared date header.
 *
 * Only the first trace receives per-point `text` labels and a `%{text}` hovertemplate
 * prefix. Follower traces keep their existing hovertemplate without `text`.
 *
 * @param traces - Traces after relation enrichment.
 * @param displayTimezone - Effective IANA timezone; skips enrichment when absent.
 * @param labelMode - Conditional or global suffix mode.
 * @param visibleXRangeMs - Visible range used to infer hover label granularity.
 * @param locale - Optional BCP 47 locale tag(s).
 * @returns Traces with leader/follower hover templates.
 */
export function enrichTimeseriesTracesWithUnifiedHoverLabels<
  T extends TimeseriesHoverTraceLike,
>(
  traces: readonly T[],
  displayTimezone: string | undefined,
  labelMode: TimeseriesAxisLabelMode,
  visibleXRangeMs?: EpochMillisecondRange,
  locale?: string | string[]
): T[] {
  if (!displayTimezone) {
    return [...traces];
  }

  const granularity = resolveTimeseriesHoverGranularity(visibleXRangeMs);
  const leaderIndex = resolveTimeseriesHoverLeaderIndex(traces);

  if (leaderIndex === -1) {
    return traces.map((trace) => {
      const normalizedX = normalizeTraceXToEpochMs(trace.x);

      return normalizedX !== undefined ? { ...trace, x: normalizedX } : trace;
    });
  }

  return traces.map((trace, index) => {
    const normalizedX = normalizeTraceXToEpochMs(trace.x);
    const withNormalizedX =
      normalizedX !== undefined ? { ...trace, x: normalizedX } : trace;

    if (index !== leaderIndex) {
      return {
        ...withNormalizedX,
        hovertemplate: buildTimeseriesFollowerHoverTemplate(
          typeof trace.hovertemplate === 'string' ? trace.hovertemplate : undefined
        ),
      };
    }

    const epochMsValues = toTraceEpochMsValues(trace.x);
    const labels = buildTimeseriesHoverLabelStrings({
      epochMsValues,
      displayTimezone,
      labelMode,
      granularity,
      locale,
    });

    return {
      ...withNormalizedX,
      text: [...labels],
      hovertemplate: buildTimeseriesLeaderHoverTemplate(
        typeof trace.hovertemplate === 'string' ? trace.hovertemplate : undefined
      ),
    };
  });
}

/**
 * Normalizes additional trace x values to epoch ms without hover enrichment.
 *
 * @param traces - Threshold overlays and other non-sensor traces.
 * @returns Traces with numeric epoch-ms x coordinates when applicable.
 */
export function normalizeTimeseriesAdditionalTracesX<
  T extends Partial<Plotly.PlotData>,
>(traces: readonly T[]): T[] {
  return traces.map((trace) => {
    const normalizedX = normalizeTraceXToEpochMs(trace.x);

    if (normalizedX === undefined) {
      return trace;
    }

    return {
      ...trace,
      x: normalizedX,
    };
  });
}

/**
 * Resolves hover label granularity from the visible x-axis range.
 *
 * @param visibleXRangeMs - Visible epoch-ms range, when known.
 * @returns Granularity aligned with axis tick selection.
 */
export function resolveTimeseriesHoverGranularity(
  visibleXRangeMs?: EpochMillisecondRange
): TimeseriesTickGranularity {
  if (!visibleXRangeMs) {
    return DEFAULT_HOVER_GRANULARITY;
  }

  return resolveGranularityFromStepMs(selectTickStepMs(visibleXRangeMs));
}

/** One trace row in the timeseries Plotly hover debug summary. */
export interface TimeseriesPlotlyHoverTraceDebug {
  readonly name: string | undefined;
  readonly type: string | undefined;
  readonly hovertemplate: string | undefined;
  readonly textSample: readonly unknown[];
  readonly xSample: readonly unknown[];
  readonly customdataSample: readonly unknown[];
  readonly checks: {
    readonly hovertemplateUsesTextHeader: boolean;
    readonly hasTextLabels: boolean;
    readonly textLooksFormatted: boolean;
    readonly customdataHasFormattedDatePrefix: boolean;
    readonly xValuesAreEpochMs: boolean;
    readonly isScatterGl: boolean;
  };
}

/** Debug summary for timeseries unified-hover payload passed to Plotly. */
export interface TimeseriesPlotlyHoverDebugSummary {
  readonly hovermode: string | undefined;
  readonly xaxis: {
    readonly type: string | undefined;
    readonly hoverformat: string | undefined;
    readonly tickmode: string | undefined;
    readonly tickvalsSample: readonly unknown[];
    readonly ticktextSample: readonly unknown[];
  };
  readonly traces: readonly TimeseriesPlotlyHoverTraceDebug[];
  readonly diagnostics: readonly string[];
  readonly expectedWhenWorking: {
    readonly hovermode: 'x unified';
    readonly xaxisHoverformat: "' ' (single space)";
    readonly xaxisType: 'date';
    readonly leaderTraceHovertemplatePrefix: '%{text}<br>';
    readonly followerTraceHovertemplatePrefix: 'none';
    readonly traceCustomdata: 'relation/threshold fields only: no date strings';
    readonly traceType: 'scatter preferred over scattergl for hover labels';
  };
}

function looksLikeFormattedTimeseriesLabel(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  if (/^\d{10,13}$/.test(value.trim())) {
    return false;
  }

  return /[A-Za-z]/.test(value) || value.includes(':') || value.includes(',');
}

function looksLikeEpochMs(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 1_000_000_000_000;
}

function customdataContainsFormattedDatePrefix(customdata: unknown): boolean {
  if (!Array.isArray(customdata)) {
    return false;
  }

  return customdata.some((entry) => {
    if (typeof entry === 'string') {
      return looksLikeFormattedTimeseriesLabel(entry);
    }

    if (Array.isArray(entry) && typeof entry[0] === 'string') {
      return looksLikeFormattedTimeseriesLabel(entry[0]);
    }

    return false;
  });
}

/**
 * Builds a compact debug summary of the Plotly payload for timeseries unified hover.
 *
 * @param data - Plotly traces passed to `Plotly.react`.
 * @param layout - Plotly layout passed to `Plotly.react`.
 * @returns Structured summary with pass/fail diagnostics.
 */
export function buildTimeseriesPlotlyHoverDebugSummary(
  data: readonly Plotly.Data[],
  layout: Partial<Plotly.Layout>
): TimeseriesPlotlyHoverDebugSummary {
  const traces = data.map((rawTrace) => {
    const trace = rawTrace as Plotly.PlotData;
    const textSample = Array.isArray(trace.text)
      ? trace.text.slice(0, 2)
      : trace.text !== undefined
        ? [trace.text]
        : [];
    const xSample = Array.isArray(trace.x) ? trace.x.slice(0, 2) : [];
    const customdataSample = Array.isArray(trace.customdata)
      ? trace.customdata.slice(0, 2)
      : trace.customdata !== undefined
        ? [trace.customdata]
        : [];
    const hovertemplate =
      typeof trace.hovertemplate === 'string' ? trace.hovertemplate : undefined;

    return {
      name: trace.name,
      type: trace.type,
      hovertemplate,
      textSample,
      xSample,
      customdataSample,
      checks: {
        hovertemplateUsesTextHeader: hovertemplate?.startsWith('%{text}<br>') ?? false,
        hasTextLabels: textSample.length > 0,
        textLooksFormatted: textSample.every(looksLikeFormattedTimeseriesLabel),
        customdataHasFormattedDatePrefix:
          customdataContainsFormattedDatePrefix(trace.customdata),
        xValuesAreEpochMs:
          xSample.length > 0 && xSample.every(looksLikeEpochMs),
        isScatterGl: trace.type === 'scattergl',
      },
    };
  });

  const diagnostics: string[] = [];

  if (layout.hovermode !== 'x unified') {
    diagnostics.push(`layout.hovermode is "${layout.hovermode}" (expected "x unified")`);
  }

  if (layout.xaxis?.type !== 'date') {
    diagnostics.push(
      `layout.xaxis.type is ${JSON.stringify(layout.xaxis?.type)} (expected "date")`
    );
  }

  if (layout.xaxis?.hoverformat !== TIMESERIES_XAXIS_HOVERFORMAT) {
    diagnostics.push(
      `layout.xaxis.hoverformat is ${JSON.stringify(layout.xaxis?.hoverformat)} (expected " ")`
    );
  }

  traces.forEach((trace, index) => {
    const label = trace.name ?? '(unnamed trace)';
    const isLeader = index === 0;

    if (isLeader && !trace.checks.hovertemplateUsesTextHeader) {
      diagnostics.push(
        `${label}: leader trace hovertemplate must start with "%{text}<br>"`
      );
    }

    if (!isLeader && trace.checks.hovertemplateUsesTextHeader) {
      diagnostics.push(
        `${label}: follower trace must not include "%{text}<br>" (avoids repeated dates)`
      );
    }

    if (isLeader && !trace.checks.hasTextLabels) {
      diagnostics.push(
        `${label}: leader trace missing trace.text: displayTimezone enrichment likely did not run`
      );
    }

    if (!isLeader && trace.checks.hasTextLabels) {
      diagnostics.push(
        `${label}: follower trace should not define trace.text`
      );
    }

    if (isLeader && trace.checks.hasTextLabels && !trace.checks.textLooksFormatted) {
      diagnostics.push(
        `${label}: trace.text does not look formatted (check displayTimezone / locale)`
      );
    }

    if (trace.checks.customdataHasFormattedDatePrefix) {
      diagnostics.push(
        `${label}: customdata still contains formatted date strings (deprecated customdata-header path)`
      );
    }

    if (trace.checks.isScatterGl) {
      diagnostics.push(
        `${label}: type is scattergl; unified hover labels may not render; prefer scatter`
      );
    }
  });

  if (diagnostics.length === 0) {
    diagnostics.push('All checks passed; tooltip should show one formatted date, no epoch header.');
  }

  return {
    hovermode:
      typeof layout.hovermode === 'string' ? layout.hovermode : undefined,
    xaxis: {
      type: layout.xaxis?.type,
      hoverformat: layout.xaxis?.hoverformat,
      tickmode: layout.xaxis?.tickmode,
      tickvalsSample: Array.isArray(layout.xaxis?.tickvals)
        ? layout.xaxis.tickvals.slice(0, 3)
        : [],
      ticktextSample: Array.isArray(layout.xaxis?.ticktext)
        ? layout.xaxis.ticktext.slice(0, 3)
        : [],
    },
    traces,
    diagnostics,
    expectedWhenWorking: {
      hovermode: 'x unified',
      xaxisHoverformat: "' ' (single space)",
      xaxisType: 'date',
      leaderTraceHovertemplatePrefix: '%{text}<br>',
      followerTraceHovertemplatePrefix: 'none',
      traceCustomdata: 'relation/threshold fields only: no date strings',
      traceType: 'scatter preferred over scattergl for hover labels',
    },
  };
}

/** Default number of points/ticks kept in the Plotly PoC debug payload. */
export const PLOTLY_POC_DEBUG_LIMIT = 5;

/** Exact Plotly.react payload truncated for standalone PoC reproduction. */
export interface TimeseriesPlotlyPocPayload {
  readonly data: readonly Record<string, unknown>[];
  readonly layout: Record<string, unknown>;
  readonly config: Record<string, unknown> | undefined;
  readonly meta: {
    readonly truncatedTo: number;
    readonly originalTraceCount: number;
    readonly originalPointCountsByTrace: readonly {
      readonly name: string | undefined;
      readonly points: number;
    }[];
    readonly originalTickCount: number | undefined;
  };
  readonly pocUsage: string;
}

const TRACE_POC_ARRAY_KEYS = [
  'x',
  'y',
  'text',
  'customdata',
  'hovertext',
  'ids',
] as const;

const TRACE_POC_SCALAR_KEYS = [
  'type',
  'mode',
  'name',
  'hovertemplate',
  'xaxis',
  'yaxis',
  'legendgroup',
  'fill',
  'fillcolor',
  'opacity',
  'line',
  'marker',
] as const;

const META_POC_ARRAY_KEYS = ['rowIndices'] as const;

const LAYOUT_POC_TOP_LEVEL_KEYS = ['hovermode', 'hoverlabel', 'xaxis'] as const;

const XAXIS_POC_ARRAY_KEYS = ['tickvals', 'ticktext'] as const;

const XAXIS_POC_SCALAR_KEYS = [
  'type',
  'tickmode',
  'hoverformat',
  'range',
  'showspikes',
  'spikemode',
  'domain',
  'title',
] as const;

function serializePlotlyDebugValue(value: unknown): unknown {
  if (value instanceof Date) {
    return value.getTime();
  }

  if (Array.isArray(value)) {
    return value.map(serializePlotlyDebugValue);
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        serializePlotlyDebugValue(entry),
      ])
    );
  }

  return value;
}

function truncatePlotlyDebugArray(
  values: unknown,
  limit: number
): unknown[] | undefined {
  if (!Array.isArray(values)) {
    return undefined;
  }

  return values
    .slice(0, limit)
    .map((entry) => serializePlotlyDebugValue(entry));
}

function truncateMarkerForPoc(
  marker: unknown,
  limit: number
): Record<string, unknown> | undefined {
  if (!marker || typeof marker !== 'object') {
    return undefined;
  }

  const source = marker as Record<string, unknown>;
  const next: Record<string, unknown> = {};

  Object.entries(source).forEach(([key, value]) => {
    if (key === 'line' && value && typeof value === 'object') {
      const line = value as Record<string, unknown>;
      next.line = Object.fromEntries(
        Object.entries(line).map(([lineKey, lineValue]) => [
          lineKey,
          Array.isArray(lineValue)
            ? truncatePlotlyDebugArray(lineValue, limit)
            : serializePlotlyDebugValue(lineValue),
        ])
      );
      return;
    }

    next[key] = Array.isArray(value)
      ? truncatePlotlyDebugArray(value, limit)
      : serializePlotlyDebugValue(value);
  });

  return next;
}

function truncateMetaForPoc(
  meta: unknown,
  limit: number
): Record<string, unknown> | undefined {
  if (!meta || typeof meta !== 'object') {
    return undefined;
  }

  const source = meta as Record<string, unknown>;
  const next: Record<string, unknown> = {};

  Object.entries(source).forEach(([key, value]) => {
    if (
      META_POC_ARRAY_KEYS.includes(key as (typeof META_POC_ARRAY_KEYS)[number]) &&
      Array.isArray(value)
    ) {
      next[key] = truncatePlotlyDebugArray(value, limit);
      return;
    }

    next[key] = serializePlotlyDebugValue(value);
  });

  return next;
}

function truncateTraceForPoc(
  rawTrace: Plotly.Data,
  limit: number
): Record<string, unknown> {
  const trace = rawTrace as Plotly.PlotData & { meta?: unknown };
  const next: Record<string, unknown> = {};

  TRACE_POC_SCALAR_KEYS.forEach((key) => {
    const value = trace[key as keyof Plotly.PlotData];
    if (value === undefined) {
      return;
    }

    if (key === 'marker') {
      next.marker = truncateMarkerForPoc(value, limit);
      return;
    }

    next[key] = serializePlotlyDebugValue(value);
  });

  const meta = truncateMetaForPoc(trace.meta, limit);
  if (meta) {
    next.meta = meta;
  }

  TRACE_POC_ARRAY_KEYS.forEach((key) => {
    const value = trace[key as keyof Plotly.PlotData];
    const truncated = truncatePlotlyDebugArray(value, limit);
    if (truncated !== undefined) {
      next[key] = truncated;
    }
  });

  return next;
}

function truncateXAxisForPoc(
  xaxis: Partial<Plotly.Layout['xaxis']> | undefined,
  limit: number
): Record<string, unknown> | undefined {
  if (!xaxis) {
    return undefined;
  }

  const next: Record<string, unknown> = {};

  XAXIS_POC_SCALAR_KEYS.forEach((key) => {
    const value = xaxis[key as keyof Plotly.Layout['xaxis']];
    if (value !== undefined) {
      next[key] = serializePlotlyDebugValue(value);
    }
  });

  XAXIS_POC_ARRAY_KEYS.forEach((key) => {
    const value = xaxis[key as keyof Plotly.Layout['xaxis']];
    const truncated = truncatePlotlyDebugArray(value, limit);
    if (truncated !== undefined) {
      next[key] = truncated;
    }
  });

  return next;
}

function truncateLayoutForPoc(
  layout: Partial<Plotly.Layout>,
  limit: number
): Record<string, unknown> {
  const next: Record<string, unknown> = {};

  LAYOUT_POC_TOP_LEVEL_KEYS.forEach((key) => {
    if (key === 'xaxis') {
      const xaxis = truncateXAxisForPoc(layout.xaxis, limit);
      if (xaxis) {
        next.xaxis = xaxis;
      }
      return;
    }

    const value = layout[key as keyof Plotly.Layout];
    if (value !== undefined) {
      next[key] = serializePlotlyDebugValue(value);
    }
  });

  return next;
}

/**
 * Builds an exact `Plotly.react(data, layout, config)` payload truncated for PoC reproduction.
 *
 * Arrays such as `x`, `y`, `text`, `customdata`, `tickvals` and `ticktext` are capped at `limit`
 * entries (default 5). Date values are serialized to epoch milliseconds.
 *
 * @param data - Plotly traces passed to `Plotly.react`.
 * @param layout - Plotly layout passed to `Plotly.react`.
 * @param config - Optional Plotly config passed to `Plotly.react`.
 * @param limit - Maximum entries kept in per-point and tick arrays.
 * @returns Copy-pasteable payload for an independent Plotly PoC.
 */
export function buildTimeseriesPlotlyPocPayload(
  data: readonly Plotly.Data[],
  layout: Partial<Plotly.Layout>,
  config?: Partial<Plotly.Config>,
  limit = PLOTLY_POC_DEBUG_LIMIT
): TimeseriesPlotlyPocPayload {
  const pocData = data.map((trace) => truncateTraceForPoc(trace, limit));
  const pocLayout = truncateLayoutForPoc(layout, limit);
  const pocConfig =
    config === undefined
      ? undefined
      : (serializePlotlyDebugValue(config) as Record<string, unknown>);

  const originalPointCountsByTrace = data.map((rawTrace) => {
    const trace = rawTrace as Plotly.PlotData;
    const xValues = Array.isArray(trace.x) ? trace.x : [];

    return {
      name: trace.name,
      points: xValues.length,
    };
  });

  return {
    data: pocData,
    layout: pocLayout,
    config: pocConfig,
    meta: {
      truncatedTo: limit,
      originalTraceCount: data.length,
      originalPointCountsByTrace,
      originalTickCount: Array.isArray(layout.xaxis?.tickvals)
        ? layout.xaxis.tickvals.length
        : undefined,
    },
    pocUsage:
      'Plotly.newPlot("myDiv", payload.data, payload.layout, payload.config)',
  };
}
